// Datos de `imagenes_generadas` que las CARDS de la columna necesitan para sus acciones
// rápidas —descargar y enviar a lanzamiento— y que la grilla no tiene
// (ADR-2026-09-07-acciones-rapidas-en-la-card).
//
// La celda de la columna nace de dos fuentes distintas: una tanda de esta sesión (que
// solo sabe id y src) y el listado de creaciones (que trae lo justo para dibujar). Para
// descargar hace falta el `file_name` real, y para elegir a qué producto va la imagen,
// sus variantes. En vez de engordar las dos fuentes, se piden acá en UNA query por lote
// de ids visibles — el mismo patrón que `useVerificacion`: solo lo que no se conoce, y
// nunca dos veces lo mismo.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { fetchInChunks } from '../../../utils/supabaseChunkedIn';

/** Lo que la card necesita saber de la fila para actuar sobre ella. */
export interface DatosCelda {
  id: string;
  /** Nombre del archivo en Storage; sin esto no hay descarga posible. */
  fileName: string | null;
  tipo: string | null;
  varianteId: string | null;
  /** Todos los productos de la generación (ADR-2026-09-06-variantes-ids-columna-aditiva). */
  variantesIds: string[] | null;
  datosGeneracion: unknown;
  /** Nombre del producto principal, para el archivo descargado. */
  varianteNombre: string | null;
  /**
   * Los cuatro campos de abajo no los usa la card: son los que le faltaban a esta fila
   * para poder ABRIR el visor sin que él salga a buscarla de nuevo —
   * ADR-2026-09-08-el-visor-abre-con-la-fila-que-el-panel-ya-tenia. Vienen en la misma
   * query, así que salen gratis.
   */
  createdAt: string | null;
  formato: string | null;
  favorito: boolean;
  upscaledPath: string | null;
  /** Quién la pidió, para "Generada por" en los equipos; viaja al visor con la fila. */
  userId?: string | null;
  /**
   * `false` si la creación fue ELIMINADA (el visor apaga `active` y borra el archivo): la
   * fila sigue existiendo, y quien la tenga anotada —el lienzo— la tiene que sacar
   * (ADR-2026-10-04-eliminar-saca-la-creacion-de-la-vista). Opcional: quien arma una
   * fila a mano (tests, el visor) no tiene por qué saberlo; sólo `false` significa borrada.
   */
  activo?: boolean;
}

interface FilaCruda {
  id: string;
  file_name: string | null;
  tipo: string | null;
  variante_id: string | null;
  variantes_ids: string[] | null;
  datos_generacion: unknown;
  created_at: string | null;
  user_id: string | null;
  formato: string | null;
  favorito: boolean | null;
  upscaled_path: string | null;
  active: boolean | null;
  /** El embed llega como objeto o como array de uno según cómo resuelva PostgREST. */
  variantes?: { nombre?: string | null } | Array<{ nombre?: string | null }> | null;
}

const nombreDeVariante = (v: FilaCruda['variantes']): string | null => {
  const fila = Array.isArray(v) ? v[0] : v;
  return fila?.nombre ?? null;
};

// Es el MISMO conjunto que lee el camino rápido del visor prestado
// (`GeneratedImagesHistory`, efecto de `soloVisor`): si se agregan campos allá, van acá,
// que es de donde ahora sale la fila con la que abre
// (ADR-2026-09-08-el-visor-abre-con-la-fila-que-el-panel-ya-tenia).
const COLUMNAS =
  'id, file_name, tipo, variante_id, variantes_ids, datos_generacion, created_at, user_id, formato, favorito, upscaled_path, active, variantes:variantes(nombre)';

export function useDatosDeCeldas(
  ids: readonly string[],
  companyId: string | null,
): Map<string, DatosCelda> {
  const [datos, setDatos] = useState<Map<string, DatosCelda>>(() => new Map());
  // Ids ya pedidos (aunque la respuesta no haya llegado): sin esto, cada re-render con
  // los mismos visibles dispararía la query de nuevo.
  const pedidos = useRef<Set<string>>(new Set());
  // Empresa de lo que hay en `pedidos`/`datos`. El reseteo NO puede vivir en su propio
  // efecto: los ids visibles suelen ser la MISMA referencia después de cambiar de
  // empresa, así que el efecto que pide no volvería a correr y la columna quedaría sin
  // datos hasta que el usuario scrollee.
  const empresaVigente = useRef<string | null>(companyId);

  const traer = useCallback(
    async (nuevos: string[]) => {
      if (!companyId || nuevos.length === 0) return;
      for (const id of nuevos) pedidos.current.add(id);
      try {
        const filas = await fetchInChunks<FilaCruda>(
          nuevos,
          (chunk) =>
            // El cast es el mismo que hace `useCreacionesRecientes`: los tipos generados
            // de Supabase no conocen `variantes_ids` (columna nueva) y modelan el embed
            // como array.
            supabase
              .from('imagenes_generadas')
              .select(COLUMNAS)
              // Filtro por empresa aunque los ids ya vengan de esta cuenta: es la misma
              // regla que el resto de la columna, y una fila ajena no tiene que resolver.
              .eq('id_company', companyId)
              .in('id', chunk) as unknown as PromiseLike<{ data: FilaCruda[] | null; error: unknown }>,
        );
        setDatos((prev) => {
          const next = new Map(prev);
          for (const f of filas) {
            next.set(f.id, {
              id: f.id,
              fileName: f.file_name,
              tipo: f.tipo,
              varianteId: f.variante_id,
              variantesIds: Array.isArray(f.variantes_ids) ? f.variantes_ids : null,
              datosGeneracion: f.datos_generacion,
              varianteNombre: nombreDeVariante(f.variantes),
              createdAt: f.created_at,
              userId: f.user_id,
              formato: f.formato,
              favorito: f.favorito === true,
              upscaledPath: f.upscaled_path,
              // Sólo un `false` explícito es "borrada": null (filas viejas) cuenta como viva.
              activo: f.active !== false,
            });
          }
          return next;
        });
      } catch (e) {
        // Se vuelve a intentar cuando cambien los visibles; mientras tanto la card no
        // muestra sus acciones (mejor eso que un botón que falla al tocarlo).
        for (const id of nuevos) pedidos.current.delete(id);
        console.warn('[columna] no se pudieron traer los datos de las creaciones', e);
      }
    },
    [companyId],
  );

  useEffect(() => {
    // Cambió la empresa: lo traído es de otra cuenta y no se mezcla (misma lección que
    // ADR-2026-08-20-el-cache-de-sesion-cruzaba-cuentas). Se limpia y se vuelve a pedir
    // todo lo visible en este mismo pase.
    if (empresaVigente.current !== companyId) {
      empresaVigente.current = companyId;
      pedidos.current = new Set();
      setDatos(new Map());
    }
    const nuevos = ids.filter((id) => !pedidos.current.has(id));
    if (nuevos.length > 0) void traer(nuevos);
  }, [ids, companyId, traer]);

  return datos;
}

export default useDatosDeCeldas;
